import React, { useRef, useEffect, ReactElement, forwardRef, ForwardedRef } from 'react';

import { Box } from 'Modules/ui3';
import { multipleRef } from 'Modules/ui';

import { SlideBaseProps, SliderProps } from './types';
import './index.scss';


const SliderComponnet = <SlidePropsType extends SlideBaseProps<SlidePropsType['data']>>(
    {
        glider,
        elementsViewCount = 1,
        overflowSizePx,
        disabled,
        slidesData,
        slideComponent: Slide,
        ...props
    }: SliderProps<SlidePropsType>,
    ref?: ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const gliderRef = useRef<HTMLDivElement>();

    useEffect(() => {
        glider.mount();
    }, [glider]);

    useEffect(() => {
        if (disabled)
            glider.disable();
        else
            glider.enable();
    }, [disabled, glider]);

    useEffect(() => {
        glider.go('=0');
    }, [glider, elementsViewCount, overflowSizePx]);

    useEffect(() => {
        glider.update({
            perView: elementsViewCount,
        });
    }, [glider, slidesData, elementsViewCount, overflowSizePx, disabled]);

    return (
        <Box
            ref={multipleRef(gliderRef, ref)}
            id={glider.selector.replace('#', '')}
            className='glide'
        >
            <Box
                className='glide__track'
                data-glide-el='track'
                overflow='visible'
                width={overflowSizePx && !disabled ? `calc(100% - ${overflowSizePx}px)` : '100%'}
            >
                <Box component='ul' className='glide__slides' overflow='visible'>
                    {slidesData.map((data, slideIndex) => (
                        <li key={`slide_${slideIndex}`} className='glide__slide'>
                            <Slide data={data} {...props} />
                        </li>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export const Slider = forwardRef(SliderComponnet) as <
    SlidePropsType extends SlideBaseProps<SlidePropsType['data']>
>(
    props: SliderProps<SlidePropsType> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReactElement;
